import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Drawer,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import PageContainer from "../../../components/PageContainer";
import AppDataTable from "../../../components/AppDataTable";
import { actualizarMarca, crearMarca, desactivarMarca, getMarcas } from "../../../services/modules/Marcas";

const MarcasCatalogo = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [formData, setFormData] = useState({
    id_marca: null,
    nombre: "",
    descripcion: "",
    activo: true,
  });

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMarcas({ activos: "1" });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al cargar categorías.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openPanel = (row = null) => {
    if (row) {
      setFormData({
        id_marca: row.id_marca,
        nombre: row.nombre || "",
        descripcion: row.descripcion || "",
        activo: Boolean(row.activo),
      });
    } else {
      setFormData({
        id_marca: null,
        nombre: "",
        descripcion: "",
        activo: true,
      });
    }
    setDrawerOpen(true);
  };

  const closePanel = () => {
    if (processing) return;
    setDrawerOpen(false);
  };

  const handleSave = async () => {
    if (!String(formData.nombre || "").trim()) {
      setError("Nombre es obligatorio.");
      return;
    }
    setProcessing(true);
    setError(null);
    try {
      const payload = {
        nombre: formData.nombre,
        descripcion: formData.descripcion || null,
        activo: formData.activo ? 1 : 0,
      };

      if (formData.id_marca) {
        await actualizarMarca(formData.id_marca, payload);
      } else {
        await crearMarca(payload);
      }
      setDrawerOpen(false);
      await fetchAll();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al guardar categoría.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDisable = async (row) => {
    if (!row?.id_marca) return;
    if (!window.confirm(`¿Desactivar "${row.nombre}"?`)) return;
    setProcessing(true);
    setError(null);
    try {
      await desactivarMarca(row.id_marca);
      await fetchAll();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Error al desactivar categoría.");
    } finally {
      setProcessing(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "nombre", headerName: "Categoría", flex: 1.2, minWidth: 220, sortable: true },
      {
        field: "descripcion",
        headerName: "Descripción",
        flex: 1.8,
        minWidth: 260,
        sortable: false,
        valueGetter: (_value, row) => row?.descripcion || "",
      },
      {
        field: "acciones",
        headerName: "Acciones",
        minWidth: 220,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => openPanel(row)}>
              Editar
            </Button>
            <Button size="small" color="error" onClick={() => handleDisable(row)}>
              Desactivar
            </Button>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <PageContainer title="Catálogo de categorías">
      <Stack spacing={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack
          direction={isXs ? "column" : "row"}
          spacing={2}
          justifyContent="space-between"
          alignItems={isXs ? "stretch" : "center"}
        >
          <Typography variant="body2" color="text.secondary">
            Crea categorías para agrupar productos (independiente de tus “Marcas” actuales).
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openPanel(null)}>
            Nueva categoría
          </Button>
        </Stack>

        <AppDataTable rows={items} columns={columns} loading={loading} />
      </Stack>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closePanel}
        PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, p: 2 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>
            {formData.id_marca ? "Editar categoría" : "Nueva categoría"}
          </Typography>
          <Button startIcon={<CloseIcon />} onClick={closePanel} disabled={processing}>
            Cerrar
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nombre"
              value={formData.nombre}
              onChange={(e) => setFormData((p) => ({ ...p, nombre: e.target.value }))}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción"
              value={formData.descripcion}
              onChange={(e) => setFormData((p) => ({ ...p, descripcion: e.target.value }))}
              fullWidth
              multiline
              minRows={3}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Activo"
              value={formData.activo ? 1 : 0}
              onChange={(e) => setFormData((p) => ({ ...p, activo: Number(e.target.value) === 1 }))}
              fullWidth
            >
              <MenuItem value={1}>Activo</MenuItem>
              <MenuItem value={0}>Inactivo</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" fullWidth onClick={handleSave} disabled={processing}>
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Drawer>
    </PageContainer>
  );
};

export default MarcasCatalogo;
